// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CSSTransitionGroup } from '../../../node_module_clones/react-transition-group';
import { createStructuredSelector } from 'reselect';

// Utils
import {
    getIsPublishedPasswordProtected,
    getIsWelcomeMessageEnabled,
    getTitle,
    getWelcomeMessageText,
} from '../../../common/elements/utils/elementPropertyUtils';
import { canRead, isFullAccess } from '../../../common/permissions/permissionUtil';
import { getBoardWelcomeMessageSeen, setBoardWelcomeMessageSeen } from './welcomeMessageLocalPersistenceService';

// Selectors
import { currentBoardUnconstrainedUserPermissions } from '../../utils/permissions/elementPermissionsSelector';
import { getPublishPreviewPasswordValidated, isPublishPreviewModeEnabledSelector } from '../preview/previewSelector';

// Components
import FullScreenModal from '../../components/modal/FullScreenModal';
import StyledButton from '../../components/buttons/StyledButton';
import WelcomeMessageModifier from './WelcomeMessageModifier';

// Styles
import './WorkspaceWelcomeMessageModal.scss';

/**
 * Determines whether to show the welcome message when viewing a preview.
 */
const showPublishPreviewWelcomeMessage = (props) => {
    const { isPublishPreview, isPasswordValidated, currentBoard } = props;

    if (!isPublishPreview) return false;

    return !getIsPublishedPasswordProtected(currentBoard) || isPasswordValidated;
};

const mapStateToProps = createStructuredSelector({
    unconstrainedPermissions: currentBoardUnconstrainedUserPermissions,
    isPublishPreview: isPublishPreviewModeEnabledSelector,
    isPasswordValidated: getPublishPreviewPasswordValidated,
});

const WorkspaceWelcomeMessageModal = (props) => {
    const { currentBoardId, currentBoard, unconstrainedPermissions, isPublishPreview } = props;

    const [closed, setClosed] = React.useState(false);

    // Clear closed state on navigation
    React.useEffect(() => {
        closed && setClosed(false);
    }, [currentBoardId]);

    let content = null;

    const showWelcomeMessage =
        !closed &&
        getIsWelcomeMessageEnabled(currentBoard) &&
        // Need to wait for the password protected boards to be validated
        canRead(unconstrainedPermissions) &&
        (showPublishPreviewWelcomeMessage(props) || !isFullAccess(unconstrainedPermissions));

    // If we're using the permission link to view the board
    if (showWelcomeMessage) {
        if (getBoardWelcomeMessageSeen(currentBoardId, isPublishPreview)) {
            setClosed(true);
        } else {
            const onClose = () => {
                setBoardWelcomeMessageSeen(currentBoardId, isPublishPreview);
                setClosed(true);
            };

            // Check to see if it's already been seen (check local storage)
            const welcomeMessage = getWelcomeMessageText(currentBoard);

            content = (
                <FullScreenModal disableClose overlay className="WorkspaceWelcomeMessageModal">
                    <div className="board-title">{getTitle(currentBoard)}</div>
                    <div className="welcome-message">{welcomeMessage}</div>
                    <WelcomeMessageModifier />
                    <StyledButton className="primary" onClick={onClose}>
                        Continue
                    </StyledButton>
                </FullScreenModal>
            );
        }
    }

    return (
        <CSSTransitionGroup
            transitionName="fade-grow"
            transitionAppear
            transitionEnterTimeout={600}
            transitionLeaveTimeout={600}
            transitionAppearTimeout={600}
        >
            {content}
        </CSSTransitionGroup>
    );
};

WorkspaceWelcomeMessageModal.propTypes = {
    currentBoardId: PropTypes.string,
    currentBoard: PropTypes.object,
    unconstrainedPermissions: PropTypes.number,
    isPublishPreview: PropTypes.bool,
    isPasswordValidated: PropTypes.bool,
};

export default connect(mapStateToProps)(WorkspaceWelcomeMessageModal);
